import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatApiError } from "@/lib/api";
import GoogleAuthButton from "@/components/GoogleAuthButton";

const ALLOWED_DOMAINS = ["gmail.com", "outlook.com", "hotmail.com", "yahoo.com"];

export const Register = () => {
  const { t, lang } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    terms_disclaimer: false,
    terms_no_refund: false,
  });
  const [err, setErr] = useState("");
  const [verifyInfo, setVerifyInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const emailDomainOk = (em) => {
    const d = em.split("@")[1]?.toLowerCase();
    return d && ALLOWED_DOMAINS.includes(d);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!emailDomainOk(form.email)) {
      setErr(t("email_help"));
      return;
    }
    if (!form.terms_disclaimer || !form.terms_no_refund) {
      setErr(lang === "ar" ? "يجب الموافقة على كلا الشرطين." : "You must accept both terms.");
      return;
    }
    setBusy(true);
    try {
      const data = await register({
        name: form.name,
        email: form.email,
        password: form.password,
        terms_accepted_disclaimer: form.terms_disclaimer,
        terms_accepted_no_refund: form.terms_no_refund,
      });
      setVerifyInfo({
        link: data.verification_link,
        token: data.verification_token,
      });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  if (verifyInfo) {
    return (
      <main className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 sm:px-6 py-8 sm:py-12" data-testid="register-success-page">
        <Card className="w-full max-w-xl p-6 sm:p-8 border border-slate-200 shadow-sm">
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 mb-3 text-start">
            {t("verify_email_title")}
          </h1>
          <p className="text-sm text-slate-600 mb-6 text-start leading-relaxed">
            {t("verify_email_pending")}
          </p>
          <a
            href={verifyInfo.link}
            target="_self"
            rel="noreferrer"
            className="block break-all text-xs font-mono text-blue-600 hover:underline bg-slate-50 p-3 border border-slate-200 rounded-md mb-6"
            data-testid="register-verification-link"
          >
            {verifyInfo.link}
          </a>
          <Button
            onClick={() => navigate(`/verify-email?token=${verifyInfo.token}`)}
            className="bg-slate-900 hover:bg-slate-800 w-full h-12 text-base"
            data-testid="register-go-verify-btn"
          >
            {t("verify_email_title")}
          </Button>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 sm:px-6 py-8 sm:py-12" data-testid="register-page">
      <Card className="w-full max-w-lg p-6 sm:p-8 border border-slate-200 shadow-sm">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-2 text-start">
          {t("register_title")}
        </h1>
        <p className="text-sm text-slate-600 mb-6 text-start">
          {t("has_account")}{" "}
          <Link to="/login" className="text-blue-600 hover:underline" data-testid="register-go-login-link">
            {t("cta_login")}
          </Link>
        </p>

        {/* Google one-click sign-up */}
        <GoogleAuthButton testId="register-google-btn" />

        <div className="relative my-6 flex items-center" data-testid="register-divider">
          <div className="flex-1 border-t border-slate-200" />
          <span className="px-3 text-xs uppercase tracking-wide text-slate-400">
            {lang === "ar" ? "أو" : "or"}
          </span>
          <div className="flex-1 border-t border-slate-200" />
        </div>

        {err && (
          <Alert variant="destructive" className="mb-4" data-testid="register-error-alert">
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
          <div className="space-y-2">
            <Label htmlFor="reg-name" className="text-start block">{t("name_label")}</Label>
            <Input
              id="reg-name"
              value={form.name}
              onChange={(e) => onChange("name", e.target.value)}
              required
              minLength={2}
              data-testid="register-name-input"
              className="h-11"
              style={{ fontSize: "16px" }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-email" className="text-start block">{t("email_label")}</Label>
            <Input
              id="reg-email"
              type="email"
              value={form.email}
              onChange={(e) => onChange("email", e.target.value)}
              required
              data-testid="register-email-input"
              className="h-11"
              style={{ fontSize: "16px" }}
            />
            <p className="text-xs text-slate-500 text-start">{t("email_help")}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-pass" className="text-start block">{t("password_label")}</Label>
            <Input
              id="reg-pass"
              type="password"
              value={form.password}
              onChange={(e) => onChange("password", e.target.value)}
              required
              minLength={8}
              data-testid="register-password-input"
              className="h-11"
              style={{ fontSize: "16px" }}
            />
          </div>

          {/* Terms — both must be checked (Arabic verbatim) */}
          <div className="space-y-4 pt-2">
            <div className="flex items-start gap-3">
              <Checkbox
                id="terms-disclaimer"
                checked={form.terms_disclaimer}
                onCheckedChange={(v) => onChange("terms_disclaimer", !!v)}
                data-testid="terms-disclaimer-checkbox"
                className="mt-1"
              />
              <Label
                htmlFor="terms-disclaimer"
                className="text-xs text-slate-700 leading-relaxed font-normal text-start cursor-pointer"
              >
                {t("terms_disclaimer")}
              </Label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox
                id="terms-norefund"
                checked={form.terms_no_refund}
                onCheckedChange={(v) => onChange("terms_no_refund", !!v)}
                data-testid="terms-norefund-checkbox"
                className="mt-1"
              />
              <Label
                htmlFor="terms-norefund"
                className="text-xs text-slate-700 leading-relaxed font-normal text-start cursor-pointer"
              >
                {t("terms_no_refund")}
              </Label>
            </div>
          </div>

          <Button
            type="submit"
            disabled={
              busy || !form.terms_disclaimer || !form.terms_no_refund
            }
            className="w-full bg-slate-900 hover:bg-slate-800 h-12 text-base"
            data-testid="register-submit-btn"
          >
            {busy ? t("loading") : t("submit_register")}
          </Button>
        </form>
      </Card>
    </main>
  );
};

export default Register;
